import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import {
  CABINET_PATHS,
  CABINET_PLATFORM,
} from '@nebalkon-workspace/cabinet-core';
import { CabinetShellChromeService } from './cabinet-shell-chrome.service';
import { CabinetWalletTopupFlowService } from '../wallet/cabinet-wallet-topup-flow.service';
import {
  CLIENT_CABINET_BOTTOM_TABS,
  clientCabinetNavLink,
  isClientCabinetNavActive,
  isClientCabinetTabRoot,
  resolveClientCabinetBackRouteFromUrl,
  shouldHideClientCabinetPageHeader,
  shouldShowClientCabinetBottomTabs,
} from '../navigation/client-cabinet-nav.util';
import { CABINET_CLIENT_SHELL_SHOW_WEB_HEADER } from './cabinet-shell-web-header.token';

@Component({
  selector: 'lib-cabinet-client-shell',
  imports: [RouterOutlet, RouterLink, TranslatePipe],
  templateUrl: './cabinet-client-shell.component.html',
  styleUrl: './cabinet-client-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CabinetClientShellComponent {
  readonly #router = inject(Router);
  readonly #topUpFlow = inject(CabinetWalletTopupFlowService);
  readonly #shellChrome = inject(CabinetShellChromeService);
  readonly #paths = inject(CABINET_PATHS);
  readonly #platform = inject(CABINET_PLATFORM);

  protected readonly showWebHeader = inject(CABINET_CLIENT_SHELL_SHOW_WEB_HEADER);
  protected readonly isMobileApp = this.#platform === 'mobile';
  protected readonly bottomTabs = CLIENT_CABINET_BOTTOM_TABS;

  readonly #currentUrl = toSignal(
    this.#router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.#router.url),
    ),
    { initialValue: this.#router.url },
  );

  protected readonly showBottomTabs = computed(() =>
    shouldShowClientCabinetBottomTabs(this.#currentUrl()),
  );
  protected readonly bottomTabsReplaced = this.#shellChrome.bottomTabsReplaced;

  constructor() {
    effect(() => {
      const url = this.#currentUrl();
      const isTabRoot = isClientCabinetTabRoot(url, this.#paths);

      this.#shellChrome.setHidePageHeaderOnTabRoot(
        shouldHideClientCabinetPageHeader(url, this.#paths),
      );

      if (isTabRoot) {
        this.#shellChrome.setBackRoute(null);
        this.#shellChrome.setUseHistoryBack(false);
        return;
      }

      this.#shellChrome.setBackRoute(
        resolveClientCabinetBackRouteFromUrl(url, this.#paths),
      );
      this.#shellChrome.setUseHistoryBack(this.#isTariffsRoute(url));
    });

    afterNextRender(() => {
      this.#handleWalletTopUpReturn();
    });

    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.#handleWalletTopUpReturn();
      });
  }

  #handleWalletTopUpReturn(): void {
    this.#topUpFlow.handlePaymentReturn(
      this.#router.parseUrl(this.#router.url).queryParams,
    );
  }

  protected navLink(route: string): string {
    return clientCabinetNavLink(route, this.#paths);
  }

  protected isNavActive(route: string, exact?: boolean): boolean {
    return isClientCabinetNavActive(
      this.#currentUrl(),
      {
        labelKey: '',
        route,
        exact,
        icon: '',
      },
      this.#paths,
    );
  }

  #isTariffsRoute(url: string): boolean {
    const normalizedUrl = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
    return normalizedUrl.startsWith(
      `${clientCabinetNavLink('tariffs', this.#paths)}`,
    );
  }
}
